import { Link } from 'react-router-dom';

// Shared layout for the /for-<persona> landing pages. Each persona page keeps
// its own module-level `const TITLE`/`const DESCRIPTION` (read by the SEO
// tracking audit + set on document.title) and passes copy in via this config.
export type PersonaConfig = {
  eyebrow: string;
  h1a: string;
  h1b?: string;
  heroSub: string;
  freeTierNote: string;
  problemHeading: string;
  problems: { t: string; d: string }[];
  useCasesHeading: string;
  useCases: { icon: string; title: string; desc: string }[];
  sampleHandle: string;
  sampleText: string;
  sampleFooter: string;
  pricingHeading: string;
  pricingCopy: string;
  faq: { q: string; a: string }[];
  ctaH2: string;
  ctaSub: string;
};

export function PersonaLayout({ config }: { config: PersonaConfig }) {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        <Hero config={config} />
        <Problem config={config} />
        <UseCases config={config} />
        <SampleAlert config={config} />
        <Pricing config={config} />
        <FAQ config={config} />
        <FinalCTA config={config} />
      </main>
      <Footer />
    </div>
  );
}

function Header() {
  return (
    <header className="border-b border-gray-200 bg-white/80 backdrop-blur-xl sticky top-0 z-40">
      <div className="mx-auto max-w-6xl flex items-center justify-between px-6 py-3">
        <Link to="/">
          <img src="/assets/logo/logo-full-dark-text.png" alt="WallaWhats" className="h-9" />
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/pricing" className="text-sm text-gray-600 hover:text-gray-900">Pricing</Link>
          <Link to="/signin" className="text-sm text-gray-600 hover:text-gray-900">Sign in</Link>
          <Link to="/signup" className="rounded-full bg-primary-600 px-5 py-2 text-xs font-semibold text-white hover:bg-primary-700">
            Start free
          </Link>
        </div>
      </div>
    </header>
  );
}

function Hero({ config }: { config: PersonaConfig }) {
  return (
    <section className="mx-auto max-w-4xl px-6 pt-16 pb-12 sm:pt-24 sm:pb-16 text-center">
      <p className="text-xs font-semibold uppercase tracking-widest text-primary-600">{config.eyebrow}</p>
      <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold tracking-tight text-gray-900 leading-tight">
        {config.h1a}{config.h1b && (<><br className="hidden sm:block" /> {config.h1b}</>)}
      </h1>
      <p className="mt-5 text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">{config.heroSub}</p>
      <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
        <Link to="/signup" className="rounded-full bg-primary-600 px-8 py-3 text-sm font-semibold text-white hover:bg-primary-700 transition-colors">
          Start free
        </Link>
        <Link to="/pricing" className="rounded-full border border-gray-300 px-8 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors">
          See plans
        </Link>
      </div>
      <p className="mt-4 text-xs text-gray-400">{config.freeTierNote}</p>
    </section>
  );
}

function Problem({ config }: { config: PersonaConfig }) {
  return (
    <section className="bg-gray-50 py-16 sm:py-20">
      <div className="mx-auto max-w-4xl px-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center">{config.problemHeading}</h2>
        <ul className="mt-10 grid gap-6 sm:grid-cols-2">
          {config.problems.map(({ t, d }) => (
            <li key={t} className="rounded-xl bg-white p-6 border border-gray-200">
              <h3 className="text-base font-semibold text-gray-900">{t}</h3>
              <p className="mt-2 text-sm text-gray-600">{d}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function UseCases({ config }: { config: PersonaConfig }) {
  return (
    <section className="py-16 sm:py-20">
      <div className="mx-auto max-w-5xl px-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center">{config.useCasesHeading}</h2>
        <div className="mt-12 grid gap-8 md:grid-cols-3">
          {config.useCases.map(({ icon, title, desc }) => (
            <div key={title} className="rounded-xl border border-gray-200 p-6 hover:border-gray-300 transition-colors">
              <div className="text-3xl">{icon}</div>
              <h3 className="mt-4 text-base font-semibold text-gray-900">{title}</h3>
              <p className="mt-2 text-sm text-gray-600 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SampleAlert({ config }: { config: PersonaConfig }) {
  return (
    <section className="bg-gray-50 py-16 sm:py-20">
      <div className="mx-auto max-w-3xl px-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center">What an alert looks like</h2>
        <p className="mt-4 text-base text-gray-600 text-center">Seconds after the post goes live on X.</p>
        <div className="mt-10 mx-auto max-w-md rounded-2xl bg-[#075e54] p-4 shadow-xl">
          <div className="rounded-xl bg-white p-4 text-sm text-gray-800">
            <div className="font-semibold text-[#075e54]">WallaWhats</div>
            <div className="mt-1 text-xs text-gray-500">{config.sampleHandle} · just now</div>
            <p className="mt-2 leading-relaxed">{config.sampleText}</p>
            <a href="#" className="mt-2 block text-xs text-blue-600">View on X →</a>
          </div>
        </div>
        <p className="mt-6 text-sm text-gray-600 text-center max-w-md mx-auto">{config.sampleFooter}</p>
      </div>
    </section>
  );
}

function Pricing({ config }: { config: PersonaConfig }) {
  return (
    <section className="py-16 sm:py-20">
      <div className="mx-auto max-w-4xl px-6 text-center">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">{config.pricingHeading}</h2>
        <p className="mt-4 text-base text-gray-600 max-w-2xl mx-auto">{config.pricingCopy}</p>
        <Link to="/pricing" className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary-600 px-6 py-3 text-sm font-semibold text-white hover:bg-primary-700 transition-colors">
          Compare plans
        </Link>
      </div>
    </section>
  );
}

function FAQ({ config }: { config: PersonaConfig }) {
  return (
    <section className="bg-gray-50 py-16 sm:py-20">
      <div className="mx-auto max-w-3xl px-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center">FAQ</h2>
        <dl className="mt-10 space-y-6">
          {config.faq.map(({ q, a }) => (
            <div key={q}>
              <dt className="text-base font-semibold text-gray-900">{q}</dt>
              <dd className="mt-2 text-sm text-gray-600 leading-relaxed">{a}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

function FinalCTA({ config }: { config: PersonaConfig }) {
  return (
    <section className="py-16 sm:py-20">
      <div className="mx-auto max-w-3xl px-6 text-center">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">{config.ctaH2}</h2>
        <p className="mt-3 text-base text-gray-600">{config.ctaSub}</p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/signup" className="rounded-full bg-primary-600 px-8 py-3 text-sm font-semibold text-white hover:bg-primary-700 transition-colors">
            Start free
          </Link>
          <Link to="/docs/getting-started" className="rounded-full border border-gray-300 px-8 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors">
            Read the docs
          </Link>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-gray-200 bg-gray-50 py-10">
      <div className="mx-auto max-w-6xl flex flex-col sm:flex-row items-center justify-between px-6 gap-4">
        <p className="text-xs text-gray-500">© 2026 WallaWhats by BWS. All rights reserved.</p>
        <div className="flex gap-5 text-xs text-gray-500">
          <Link to="/" className="hover:text-gray-900">Home</Link>
          <Link to="/pricing" className="hover:text-gray-900">Pricing</Link>
          <Link to="/blog/" className="hover:text-gray-900">Blog</Link>
          <Link to="/docs/overview" className="hover:text-gray-900">Docs</Link>
          <Link to="/privacy-policy" className="hover:text-gray-900">Privacy</Link>
        </div>
      </div>
    </footer>
  );
}
